/**
 * An enum with all breakpoints and their sizes.
 *
 * @enum BREAKPOINT
 * @type {{xl: number, md: number, sm: number, xs: number, lg: number, xxl: number}}
 */
export const BREAKPOINT = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1201,
  xxl: 1600,
};

/**
 * Given a breakpoint key (i.e. 'md', 'sm', etc) it will return
 * the next breakpoint (from BREAKPOINTS) or null if it does not exist.
 *
 * @param {string} breakpoint The breakpoint key (i.e. 'md', 'sm', etc).
 * @returns {null|number}
 */
export const breakpointNext = breakpoint => {
  const keys = Object.keys(BREAKPOINT);
  const index = keys.findIndex(key => key === breakpoint);

  if (index >= 0 && index < keys.length - 1) {
    const next = keys[index + 1];
    return BREAKPOINT[next];
  }

  return null;
};

/**
 * Given a breakpoint key (i.e. 'md', 'sm', etc) it will return
 * the minimum breakpoint width (from BREAKPOINTS) or null for the smallest (first)
 * breakpoint.
 *
 * @param {string} breakpoint The breakpoint key (i.e. 'md', 'sm', etc).
 * @returns {null|number}
 */
export const breakpointMin = breakpoint => {
  if (BREAKPOINT[breakpoint] === 0) {
    return null;
  }

  return BREAKPOINT[breakpoint];
};

/**
 * Given a breakpoint key (i.e. 'md', 'sm', etc) it will return
 * the maximum breakpoint width (from BREAKPOINTS) or null for the largest (last)
 * breakpoint.
 *
 * The maximum value is calculated as the minimum of the next one less 1.
 *
 * @param {string} breakpoint The breakpoint key (i.e. 'md', 'sm', etc).
 * @returns {null|number}
 */
export const breakpointMax = breakpoint => {
  const next = breakpointNext(breakpoint);

  if (next) {
    return next - 1;
  }

  return null;
};

/**
 * Media of at least the minimum breakpoint width.
 * Null for the smallest breakpoint.
 *
 * @param {string} breakpoint The breakpoint key (i.e. 'md', 'sm', etc).
 * @returns {null|{ minWidth: number }}
 */
export const breakpointUp = breakpoint => {
  const min = breakpointMin(breakpoint);

  if (min) {
    return { minWidth: min };
  }

  return null;
};

/**
 * Media of at most the maximum breakpoint width.
 * Null for the largest breakpoint.
 *
 * @param {string} breakpoint The breakpoint key (i.e. 'md', 'sm', etc).
 * @returns {null|{ maxWidth: number }}
 */
export const breakpointDown = breakpoint => {
  const max = breakpointMax(breakpoint);

  if (max) {
    return { maxWidth: max };
  }

  return null;
};
